import React, { useContext, useState, useMemo } from 'react';
import { APIContext } from '../contexts/APIContext';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  trackUserClicks,
  previewString,
  removeMarkTag,
  validateStringForXSS,
  capitalizeEveryWord,
  getValidToken,
} from '../utils';
import { NodeHtmlMarkdown } from 'node-html-markdown';
import { SourceType } from '../types';
import { ChatbotContext } from '../contexts/ChatbotContext';
import '@radix-ui/react-tooltip';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { TooltipContent } from '@radix-ui/react-tooltip';
import { Tooltip, TooltipProvider, TooltipTrigger } from './ui/Tooltip';

// Markdown component used to display markdown responses in the chat
// This component is using the react-markdown library (https://github.com/remarkjs/react-markdown)
/* eslint-disable react/prop-types */
export const GMAMarkdown = ({ markdown }) => {
  const {
    userDetails,
    chatSessionId,
    websocketConnectionRef: { current: websocket },
    chatResponse: response,
    WBUId,
    AppName,
    accounts,
  } = useContext(APIContext);
  const { sourceHistory } = useContext(ChatbotContext);
  const [source] = useState(response?.chatResponse?.responseSource ?? response?.responseSource);

  // Override react-markdown elements to add class names
  const H1 = ({ children }) => <h1 className="md-post-h1 text-2xl font-extrabold mb-4">{children}</h1>;
  const H2 = ({ children }) => <h2 className="md-post-h2 text-xl font-bold mb-3">{children}</h2>;
  const H3 = ({ children }) => <h3 className="md-post-h3 text-lg font-semibold mb-2">{children}</h3>;
  const H4 = ({ children }) => <h4 className="md-post-h4 text-base font-medium mb-1">{children}</h4>;
  const P = ({ children }) => <p className="md-post-p">{children}</p>;
  const Ol = ({ children }) => <ol className="md-post-ol list-decimal list-inside ml-6">{children}</ol>;
  const Ul = ({ children }) => <ul className="md-post-ul list-disc list-outside ml-6 mb:ml-0">{children}</ul>;
  const Li = ({ children }) => <li className="md-post-li">{children}</li>;
  const Table = ({ children }) => (
    <table className="md-post-table font-graphik w-fit border-collapse">{children}</table>
  );
  const Th = ({ children }) => <th className="md-post-th border border-white p-1 text-center">{children}</th>;
  const Td = ({ children }) => <td className="md-post-td border border-white p-1 text-center">{children}</td>;
  const Img = ({ src, alt }) => <img className="md-post-img w-1/2 h-auto" src={src} alt={alt} />;
  const A = ({ href, onClick, target = '_blank', rel = 'noopener noreferrer', children }) => {
    const lowerCaseHref = href.toLowerCase();
    if (lowerCaseHref.slice(0, 12) === 'transcriptid') {
      // const index = lowerCaseHref.indexOf('citationid');
      // const transcriptId = lowerCaseHref.slice(12, index);
      // const citationId = lowerCaseHref.slice(index + 10);
      const transcriptId = lowerCaseHref.slice(12);
      const citationNumber = children;

      const matchedSource = useMemo(() => {
        for (const source of Object.values(sourceHistory)) {
          const sourceList = source?.sourceList;
          const matched = sourceList?.find((source) => source?.id === transcriptId);
          if (matched) return matched;
        }
      }, [transcriptId, sourceHistory]);

      const citation = matchedSource?.citation ?? '';
      const transcriptPreview = matchedSource?.transcript ?? '';

      return (
        <>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <button
                  className="h-4 w-4 border flex items-center justify-center text-[10px] text-gamma-standard-purple rounded-[50%] border-solid border-gamma-standard-purple ml-1 mr-1 hover:text-white hover:bg-gamma-standard-purple tb:hidden"
                  type="button"
                  data-transcriptid={transcriptId}
                  // data-citationidfrombutton={citationId}
                >
                  {citationNumber}
                </button>
              </TooltipTrigger>
              <TooltipContent
                side="top"
                className="bg-[#fff] z-10 text-[#000] flex flex-col gap-4 text-xs border-0 max-w-[250px] drop-shadow-[0_12px_16px_rgba(0,0,0,0.12)] p-2 rounded-md m-2"
                sideOffset={3}
              >
                <div>
                  <div className="text-sm font-medium mb-1">Citation</div>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: validateStringForXSS(capitalizeEveryWord(citation)),
                    }}
                  ></div>
                </div>
                <div>
                  <div className="text-sm font-medium mb-1">Transcript Preview</div>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: validateStringForXSS(previewString(removeMarkTag(transcriptPreview))),
                    }}
                  ></div>
                </div>
                <TooltipPrimitive.Arrow width={11} height={5} style={{ fill: '#fff' }} />
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </>
      );
    } else {
      const handleClick = () => {
        const token = getValidToken();
        trackUserClicks(websocket, 'Quant Link', userDetails, chatSessionId, WBUId, AppName, accounts, token);
      };
      return (
        <a className="md-post-a underline text-[#A100FF]" href={href} target={target} rel={rel} onClick={handleClick}>
          {children}
        </a>
      );
    }
  };
  const Blockquote = ({ children }) => (
    <blockquote className="md-post-blockquote bg-gamma-divider-gray border-l-4 p-2 my-2 w-fit text-base leading-relaxed">
      {children}
    </blockquote>
  );
  const Hr = () => <hr className="md-post-hr" />;

  return (
    <>
      <Markdown
        remarkPlugins={[remarkGfm]} // Adds support for GFM (GitHub Flavored Markdown - tables, strikethroughs, etc.)
        components={{
          // Override react-markdown elements to use custom components
          h1: H1,
          h2: H2,
          h3: H3,
          h4: H4,
          p: P,
          ol: Ol,
          ul: Ul,
          li: Li,
          table: Table,
          th: Th,
          td: Td,
          img: Img,
          a: A,
          blockquote: Blockquote,
          hr: Hr,
        }}
      >
        {source === SourceType.GENAI
          ? NodeHtmlMarkdown.translate((markdown || '').replace(/(\r\n|\r|\n)/g, '<br>'))
          : markdown || ''}
        {/* {'[Earth](https://google.com)'} */}
      </Markdown>
    </>
  );
};
